import raw from './catalog.json' with { type: 'json' }

export interface Range { min: number; max: number; step: number }
export interface CatalogRanges {
  sphere: Range
  cylinder: Range
  add: Range
  axis: Range
  pupillaryDistanceMm: Range
  birthYear: Range
}
export interface Frame { id: string; productNumber: string; name: string; photo: string; colors: string[] }
export interface ClipOn { id: string; name: string; photo: string }
export interface Center { id: string; name: string }
export interface Catalog { frames: Frame[]; clipOns: ClipOn[]; centers: Center[]; ranges: CatalogRanges }

/** min..max by step, rounded to 2 dp so repeated addition cannot drift. */
export function rangeValues(r: Range): number[] {
  const out: number[] = []
  const n = Math.floor((r.max - r.min) / r.step + 1e-9)
  for (let i = 0; i <= n; i++) out.push(Math.round((r.min + i * r.step) * 100) / 100)
  return out
}

/** The colour's material swatch: frames/swatches/{frame id}-{colour, lower-case, hyphenated}.png. */
export function swatchPath(frame: Frame, color: string): string {
  return `frames/swatches/${frame.id}-${color.toLowerCase().replace(/ /g, '-')}.png`
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const arr = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? v.filter(isObj) : [])
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

const RANGE_NAMES = ['sphere', 'cylinder', 'add', 'axis', 'pupillaryDistanceMm', 'birthYear'] as const
const DECIMAL = new Set(['sphere', 'cylinder', 'add'])

function dupes(values: string[]): string[] {
  const seen = new Map<string, number>()
  for (const v of values) seen.set(v, (seen.get(v) ?? 0) + 1)
  return [...seen].filter(([, n]) => n > 1).map(([k]) => k)
}

export function checkCatalog(input: unknown): Catalog {
  if (!isObj(input)) throw new Error('catalog.json is invalid:\n- The file is empty.')
  const p: string[] = []
  const frames = arr(input.frames)
  const clipOns = arr(input.clipOns)
  const centers = arr(input.centers)

  if (frames.length === 0) p.push('No frames.')
  if (centers.length === 0) p.push('No centers.')

  const ids = [...frames, ...clipOns, ...centers].map((x) => String(x.id))
  for (const d of dupes(ids)) p.push(`Duplicate id '${d}'.`)

  for (const f of frames) {
    const colors = Array.isArray(f.colors) ? f.colors.map(String) : []
    if (colors.length === 0) p.push(`Frame '${String(f.id)}' has no colors.`)
    else for (const d of dupes(colors)) p.push(`Frame '${String(f.id)}' lists color '${d}' twice.`)
    if (typeof f.productNumber !== 'string' || f.productNumber.trim() === '')
      p.push(`Frame '${String(f.id)}' has no product number.`)
  }

  const ranges = input.ranges
  if (!isObj(ranges)) {
    p.push('No ranges.')
  } else {
    for (const name of RANGE_NAMES) {
      const r = ranges[name]
      if (!isObj(r)) p.push(`Range '${name}' is missing.`)
      else if (!isNum(r.min) || !isNum(r.max)) p.push(`Range '${name}' is malformed.`)
      else if (DECIMAL.has(name) ? !isNum(r.step) || r.step <= 0 || r.min > r.max : r.min > r.max)
        p.push(`Range '${name}' is malformed.`)
      else if (!DECIMAL.has(name)) r.step = 1
    }
  }

  if (p.length > 0) throw new Error('catalog.json is invalid:\n- ' + p.join('\n- '))
  return input as unknown as Catalog
}

export const catalog: Catalog = checkCatalog(structuredClone(raw))
